import React from 'react';
import './ErrorDisplay.css';

export type ErrorType = 'network' | 'api' | 'validation' | 'general';

interface ErrorDisplayProps {
  error: string | null;
  type?: ErrorType;
  onRetry?: () => void;
  onDismiss?: () => void;
  showDetails?: boolean;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ 
  error, 
  type = 'general', 
  onRetry, 
  onDismiss,
  showDetails = false 
}) => {
  if (!error) return null;

  const getErrorIcon = (type: ErrorType): string => {
    switch (type) {
      case 'network':
        return '🌐';
      case 'api':
        return '⚡';
      case 'validation':
        return '⚠️';
      default:
        return '❌';
    }
  };

  const getErrorTitle = (type: ErrorType): string => {
    switch (type) {
      case 'network':
        return 'ネットワークエラー';
      case 'api':
        return 'APIエラー';
      case 'validation':
        return '入力エラー';
      default:
        return 'エラーが発生しました';
    }
  };

  const getSuggestion = (type: ErrorType): string => {
    switch (type) {
      case 'network':
        return 'インターネット接続を確認してください。';
      case 'api':
        return 'しばらく待ってから再試行してください。';
      case 'validation':
        return '入力内容を確認してください。';
      default:
        return '問題が続く場合は、ページを再読み込みしてください。';
    }
  };

  return (
    <div className={`error-display error-display--${type}`}>
      <div className="error-display__content">
        <div className="error-display__header">
          <span className="error-display__icon">{getErrorIcon(type)}</span>
          <h3 className="error-display__title">{getErrorTitle(type)}</h3>
          {onDismiss && (
            <button 
              className="error-display__close"
              onClick={onDismiss}
              aria-label="エラーを閉じる"
            >
              ×
            </button>
          )}
        </div>
        
        <div className="error-display__body">
          <p className="error-display__message">{error}</p>
          <p className="error-display__suggestion">{getSuggestion(type)}</p>
        </div>
        
        {onRetry && (
          <div className="error-display__actions">
            <button 
              className="error-display__retry-btn"
              onClick={onRetry}
            >
              🔄 再試行
            </button>
          </div>
        )}
        
        {showDetails && import.meta.env.DEV && (
          <details className="error-display__details">
            <summary>技術的詳細</summary>
            <pre className="error-display__details-content">{error}</pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default ErrorDisplay;